import CreateButton from "../components/CreateButton"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import api from "../api"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as CopyIcon } from "../assets/images/icons/ic-copy.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from '../assets/images/icons/ic-edit.svg'
import { ButtonStatus } from "../common/constants"
import useContextMenu from "../common/hooks/useContextMenu"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import Breadcrumb from "../components/Breadcrumb"
import StatusBadge from "../components/StatusBadge"
import Button from "../components/Button"
import Checkbox from "../components/Checkbox"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import UserCourseDialog from "../components/dialogs/UserCourseDialog"
import Select from "../components/Select"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import MultipleSelect from "../components/MultipleSelect"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import TruncatedText from "../components/TruncatedText"
import styles from "./Course.module.css"

const Course = () => {

    const navigate = useNavigate()
    const { t } = useTranslation()
    const { width } = useWindowDimensions()
    const { courseId } = useParams();
    const [course, setCourse] = useState(null)
    const [editions, setEditions] = useState(null)
    const [loading, setLoading] = useState(true)
    const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' }
    const [alert, setAlert] = useState({ open: false, title: '', text: '' })
    const [students, setStudents] = useState(null)
    const [openStudents, setOpenStudents] = useState([])
    const [subscribedStudents, setSubscribedStudents] = useState([])
    const [editSubscription, setEditSubscription] = useState(null)
    const [subscribeDialog, setSubscribeDialog] = useState(false)
    const [studentsToSubscribe, setStudentsToSubscribe] = useState([])
    const [editionToSubscribe, setEditionToSubscribe] = useState(null)
    //

    const { showMenu } = useContextMenu()

    useEffect(() => {
        window.scrollTo({ top: 0 })
    }, [])

    const getCourseStudents = async () => {
        try {
            const students = await api.get(`/admin/courses/${courseId}/students`)
            setStudents(students)
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {

        const getCourse = async () => {
            setLoading(true)
            try {
                const course = await api.get(`/admin/courses/${courseId}`)
                setCourse(course)
            }
            catch (e) {
                console.error(e)
                navigate("/404")
            }
            setLoading(false)
        }

        const getCourseEditions = async () => {
            try {
                let editions = await api.get(`/admin/courses/${courseId}/editions`)
                editions = editions.map(edition => {
                    edition.start = getEditionStart(edition)
                    edition.end = getEditionEnd(edition)
                    return edition
                }).sort((a, b) => b.name.localeCompare(a.name))
                setEditions(editions)
            }
            catch (e) {
                console.error(e)
            }
        }

        if (!course && courseId !== 'create') {
            getCourse()
            getCourseEditions()
            getCourseStudents()
        }
    }, [courseId])

    const flatLesson = (edition) => {
        let lessons = edition.modules.flatMap(module => module.lessons || [])
        return lessons
    }

    const getEditionStart = (edition) => {
        let lessons = edition.modules.flatMap(module => module.lessons || [])
        // Filtra le lezioni che non hanno starts_at definito
        lessons = lessons.filter(lesson => lesson.starts_at);

        if (lessons.length === 0) return undefined; // Se non ci sono lezioni valide, ritorna undefined

        const objectWithMinStartAt = lessons.reduce((min, current) => {
            return new Date(current.starts_at) < new Date(min.starts_at) ? current : min;
        }, lessons[0]);

        return objectWithMinStartAt?.starts_at;
    }

    const getEditionEnd = (edition) => {
        let lessons = edition.modules.flatMap(module => module.lessons || [])
        // Filtra le lezioni che non hanno starts_at definito
        lessons = lessons.filter(lesson => lesson.starts_at);

        if (lessons.length === 0) return undefined; // Se non ci sono lezioni valide, ritorna undefined

        const objectWithMaxStartAt = lessons.reduce((max, current) => {
            return new Date(current.starts_at) > new Date(max.starts_at) ? current : max;
        }, lessons[0]);

        return objectWithMaxStartAt?.starts_at;
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'public':
                return <StatusBadge appearance="soft" color={'var(--sf-green)'} label={"PUBBLICA"} />
            case 'draft':
                return <StatusBadge appearance="soft" color={'var(--secondary)'} label={"BOZZA"} />
            case 'hidden':
                return <StatusBadge appearance="soft" color={'var(--sf-orange)'} label={"NASCOSTA"} />
            case 'ended':
                return <StatusBadge appearance="soft" color={'rgba(var(--text-color-rgb), 55%)'} label={"TERMINATA"} />
            default:
                return null
        }
    }


    const duplicateEdition = async (editionId, duplicateAll = false) => {
        try {
            const edition = await api.get(`/admin/editions/${editionId}/duplicate?duplicate_all=${duplicateAll}`)
            setEditions([...editions, edition])
        }
        catch (e) {
            console.error(e)
        }
    }

    const deleteEdition = async (editionId, duplicateAll = false) => {
        try {
            await api.delete(`/admin/editions/${editionId}`)
            setEditions(editions.filter(e => e.id !== editionId))
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (!students) return
        setSubscribedStudents(students.filter(student => student.editions.length > 0))
        setOpenStudents(students.filter(student => student.editions.length === 0))
    }, [students])

    const searchSubscribedStudents = (query) => {
        if (!subscribedStudents || !students) return
        if (query.trim().length === 0) {
            setSubscribedStudents(students.filter(student => student.editions.length > 0))
            return
        }
        else {
            const filtered = students.filter(student => student.editions.length > 0).filter(student => (`${student.name}${student.surname}`).toLowerCase().includes(query.replace(' ', '').toLowerCase()) || `${student.surname}${student.name}`.toLowerCase().includes(query.replace(' ', '').toLowerCase()))
            setSubscribedStudents(filtered)
        }
    }

    const searchOpenStudents = (query) => {
        if (!openStudents || !students) return
        if (query.trim().length === 0) {
            setOpenStudents(students.filter(student => student.editions.length === 0))
            return
        }
        else {
            const filtered = students.filter(student => student.editions.length === 0).filter(student => (`${student.name}${student.surname}`).toLowerCase().includes(query.replace(' ', '').toLowerCase()) || `${student.surname}${student.name}`.toLowerCase().includes(query.replace(' ', '').toLowerCase()))
            setOpenStudents(filtered)
        }
    }



    const [columns, setColumns] = useState([
        {
            key: 'id',
            label: 'ID',
            type: 'number',
            pinned: false
        },
        {
            key: 'name',
            label: 'NOME',
            type: 'string',
            pinned: false
        },
        {
            key: 'start',
            label: 'INIZIO',
            type: 'date',
            pinned: false
        },
        {
            key: 'end',
            label: 'FINE',
            type: 'date',
            pinned: false
        },
        {
            key: 'students',
            label: 'STUDENTI',
            type: 'number',
            pinned: false
        },
        {
            key: 'modules',
            label: 'MODULI',
            type: 'number',
            pinned: false
        },
        {
            key: 'lessons',
            label: 'LEZIONI',
            type: 'number',
            pinned: false
        },
        {
            key: 'status',
            label: 'STATO',
            type: 'custom',
            pinned: false
        },
        {
            key: 'actions',
            label: 'AZIONI',
            type: 'custom',
            pinned: false
        }
    ])

    const getActions = (edition) => {
        const actions = [
            {
                label: "Duplica",
                icon: CopyIcon,
                onClick: () => {
                    setAlert({
                        open: true,
                        title: `Duplicare ${edition.name}?`,
                        text: 'Scegli se vuoi duplicare solo l\'edizione o tutti i suoi dati inclusi moduli, docenti e lezioni.',
                        actions: [
                            {
                                label: 'SOLO EDIZIONE',
                                onClick: async () => {
                                    await duplicateEdition(edition.id, false)
                                    setAlert((prev) => {
                                        prev.open = false
                                        return { ...prev }
                                    })
                                }
                            },
                            {
                                label: 'TUTTO',
                                onClick: async () => {
                                    await duplicateEdition(edition.id, true)
                                    setAlert((prev) => {
                                        prev.open = false
                                        return { ...prev }
                                    })
                                }
                            }
                        ]
                    })
                },
            }
        ]
        if (edition.status === 'draft') {
            actions.push(
                {
                    label: "Elimina",
                    icon: DeleteIcon,
                    onClick: () => {
                        setAlert({
                            open: true,
                            title: `Eliminare ${edition.name}?`,
                            text: 'L\'operazione non potrà essere annullata',
                            actions: [
                                {
                                    label: 'ELIMINA',
                                    onClick: async () => {
                                        await deleteEdition(edition.id)
                                        setAlert((prev) => {
                                            prev.open = false
                                            return { ...prev }
                                        })
                                    },
                                    color: 'var(--sf-red)'
                                }
                            ]
                        })
                    },
                    color: 'var(--sf-red)'
                }
            )
        }
        return actions

    }

    const [subscribing, setSubscribing] = useState(false)
    const [sendSubscriptionMail, setSendSubscriptionMail] = useState(true)

    const bulkSubscribe = async () => {
        setSubscribing(true)

        for (let s of studentsToSubscribe) {
            try {
                const url = `/admin/users/${s.id}/courses/${course.id}/editions/${editionToSubscribe}?recordings=${true}&create_order=${false}&send_mail=${sendSubscriptionMail}`
                await api.post(url);
            } catch (e) {
                console.error(e)
            }
        }
        await getCourseStudents()
        setSubscribing(false)
    }

    return (
        <HeaderFooterLayout>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <header className={styles.header}>
                            <Breadcrumb items={[{ label: 'Corsi', to: '/courses' }, { label: course?.name || 'Caricamento…' }]} />
                        </header>
                        <Card appearance="modern" title="Scheda corso" actions={<Link className={styles.courseLink} to={`/shop_courses/${courseId}/editor`}>Apri nell'editor</Link>}>
                            <div className={styles.cardInner}>
                                <div className={styles.courses}>
                                    {loading &&
                                        <div className={styles.skeletonCourse}>
                                            <Skeleton type="rect" width={50} height={50} borderRadius="12px" />
                                            <div className={styles.skeletonInfo}>
                                                <Skeleton type="rect" width={150} height={20} borderRadius="4px" />
                                                <Skeleton type="rect" width={300} height={15} borderRadius="4px" />
                                            </div>
                                            <Skeleton type="rect" width={60} height={30} borderRadius="8px" />
                                            <ChevronIcon className={styles.chevron} />
                                        </div>
                                    }
                                    {!loading &&
                                        <div className={styles.course}>
                                            <img src={course.thumbnail} className={styles.thumbnail} alt="" />
                                            <div className={styles.info}>
                                                <span className={styles.courseType}>{t(`courses.types.${course.type}`)}</span>
                                                <h1 className={styles.courseTitle}>{course.name}</h1>
                                                <TruncatedText
                                                    className={styles.description}
                                                    text={course.description}
                                                    maxLength={256}
                                                />
                                            </div>
                                            <div className={styles.statusBadge}><StatusBadge appearance="soft" label={t(`status.${course.status}`)} color={course.status === 'public' ? 'var(--sf-green)' : course.status === 'draft' ? 'var(--secondary)' : 'var(--sf-red)'} /></div>
                                        </div>
                                    }
                                </div>
                            </div>
                        </Card>

                        <Card appearance="modern" integratedTable title={`Edizioni · ${editions?.length ?? 0}`} actions={
                            <CreateButton onClick={() => navigate(`/courses/${courseId}/editions/create`)}>Aggiungi edizione</CreateButton>
                        }>
                            <Table appearance="minimal" loading={!editions} loadingN={5} style={{ border: 0, boxShadow: 'none', borderRadius: 0, marginTop: 0 }} columns={columns}
                                elements={
                                    editions ? editions.map(edition => {
                                        return {
                                            ...edition,
                                            modules: edition.modules.length,
                                            lessons: flatLesson(edition).length,
                                            actions: <MeatBallsMenu style={{ padding: '0.5rem' }} actions={getActions(edition)} />,
                                            status: { value: edition.status, type: 'string', component: <div className={styles.statusBadge}>{getStatusBadge(edition.status)}</div> },
                                            href: `/courses/${courseId}/editions/${edition.id}`
                                        }
                                    }) : []
                                }
                                defaultSort={{ key: 'name', direction: 'desc' }}
                            />
                        </Card>

                        <Card appearance="modern" integratedTable title={`Studenti iscritti · ${students ? students.filter(student => student.editions.length > 0).length : 0}`} actions={
                            <CreateButton onClick={() => {
                                setSendSubscriptionMail(true)
                                setSubscribeDialog(true)
                            }}>Aggiungi studenti</CreateButton>
                        }>
                            <div className={styles.tableContent}>
                                <div className={styles.tableToolbar}><TextInput appearance="modern" type={'search'} placeholder="Cerca studente..." onKeyUp={searchSubscribedStudents} /></div>
                                <Table
                                    appearance="minimal"
                                    loading={!students}
                                    loadingN={5}
                                    style={{ maxHeight: '400px', overflowY: 'auto', border: 0, boxShadow: 'none', borderRadius: 0, marginTop: 0 }}
                                    defaultSort={{ key: 'surname', direction: 'asc' }}
                                    columns={[
                                        {
                                            key: 'id',
                                            label: 'ID',
                                            type: 'number',
                                            pinned: false
                                        },
                                        {
                                            key: 'name',
                                            label: 'NOME',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'surname',
                                            label: 'COGNOME',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'email',
                                            label: 'EMAIL',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'editions',
                                            label: 'EDIZIONI',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'last_access',
                                            label: 'ULTIMO ACCESSO',
                                            type: 'datetime',
                                            pinned: false
                                        },
                                        {
                                            key: 'actions',
                                            label: 'AZIONI',
                                            type: 'custom',
                                            pinned: false
                                        }
                                    ]}
                                    elements={
                                        subscribedStudents.map(student => {
                                            return {
                                                ...student,
                                                editions: student.editions.map(e => e.name).join(', '),
                                                href: `/users/students/${student.id}`,
                                                actions: <MeatBallsMenu style={{ padding: '0.5rem' }} actions={[
                                                    {
                                                        label: 'Apri profilo studente',
                                                        onClick: () => navigate(`/users/students/${student.id}`)
                                                    },
                                                    ...student.editions.map(edition => ({
                                                        label: `Apri edizione · ${edition.name}`,
                                                        onClick: () => navigate(`/courses/${courseId}/editions/${edition.id}`)
                                                    }))
                                                ]} />
                                            }
                                        })}
                                />
                            </div>
                        </Card>

                        <Card appearance="modern" integratedTable title={`Studenti open · ${students ? students.filter(student => student.editions.length === 0).length : 0}`}>
                            <div className={styles.tableContent}>
                                <div className={styles.tableToolbar}><TextInput appearance="modern" type={'search'} placeholder="Cerca studente..." onKeyUp={searchOpenStudents} /></div>
                                <Table
                                    appearance="minimal"
                                    loading={!students}
                                    loadingN={5}
                                    style={{ maxHeight: '400px', overflowY: 'auto', border: 0, boxShadow: 'none', borderRadius: 0, marginTop: 0 }}
                                    defaultSort={{ key: 'surname', direction: 'asc' }}
                                    columns={[
                                        {
                                            key: 'id',
                                            label: 'ID',
                                            type: 'number',
                                            pinned: false
                                        },
                                        {
                                            key: 'name',
                                            label: 'NOME',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'surname',
                                            label: 'COGNOME',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'email',
                                            label: 'EMAIL',
                                            type: 'string',
                                            pinned: false
                                        },
                                        {
                                            key: 'last_access',
                                            label: 'ULTIMO ACCESSO',
                                            type: 'datetime',
                                            pinned: false
                                        },
                                        {
                                            key: 'actions',
                                            label: 'MODIFICA',
                                            type: 'custom',
                                            pinned: false
                                        }
                                    ]}
                                    elements={
                                        openStudents.map(student => {
                                            return {
                                                ...student,
                                                href: `/users/students/${student.id}`,
                                                actions: <EditIcon style={{ width: '1rem', height: '1rem', color: 'var(--primary)', marginLeft: '1rem' }} onClick={(e) => {
                                                    setEditSubscription({
                                                        user: {
                                                            ...student, courses: student.editions.map(e => ({ ...course, edition: e })),
                                                        }, edition_id: null
                                                    })
                                                }} />
                                            }
                                        })}
                                />
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
            <AlertDialog open={alert.open} title={alert.title} text={alert.text} actions={alert.actions} onClose={() => {
                setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                })
            }} />

            {editSubscription &&
                <UserCourseDialog
                    user={editSubscription.user}
                    courses={[course]}
                    course_id={course.id}
                    edition_id={editSubscription.edition_id}
                    onClose={() => {
                        setEditSubscription(null)
                    }} />
            }

            <Dialog title={"Iscrivi studenti"}
                style={{ maxWidth: '560px', overflow: 'visible' }}
                open={subscribeDialog}
                onClose={() => {
                    setSubscribeDialog(false)
                    setSendSubscriptionMail(true)
                }} overflow="visible"
                action={
                    <div className={styles.enrollmentActions}>
                    <Button appearance="subtle" requiresEdit={false} disabled={subscribing} onClick={() => {
                        setSubscribeDialog(false)
                        setSendSubscriptionMail(true)
                    }}>Annulla</Button>
                    <Button disabled={studentsToSubscribe.length === 0 || !editionToSubscribe || subscribing} status={subscribing ? ButtonStatus.Loading : null} onClick={async () => {
                        await bulkSubscribe()
                        setSubscribeDialog(false)
                        setStudentsToSubscribe([])
                        setEditionToSubscribe(null)
                        setSendSubscriptionMail(true)
                    }}>Iscrivi studenti</Button>
                    </div>
                }>
                <div className={styles.enrollmentForm}>
                <p className={styles.enrollmentIntro}>Seleziona gli studenti da iscrivere e l’edizione di <strong>{course?.name}</strong>.</p>
                <div className={styles.enrollmentField}>
                <span>Studenti</span>
                <MultipleSelect appearance="modern"
                    placeholder="Seleziona studenti..."
                    selected={studentsToSubscribe}
                    options={openStudents.map(student => ({ id: student.id, name: `${student.name} ${student.surname}` }))} onSelect={(values) => {
                        setStudentsToSubscribe(values)
                    }} />
                <small>{studentsToSubscribe.length} selezionati</small>
                </div>
                <div className={styles.enrollmentField}>
                <span>Edizione</span>
                <Select appearance="modern"
                    selected={editions?.find(edition => edition.id === editionToSubscribe)}
                    placeholder="Seleziona edizione..."
                    options={editions ? editions.map(edition => ({ id: edition.id, name: edition.name + (edition.status === 'ended' ? ' (Terminata)' : '') })) : []}
                    onSelect={(option) => setEditionToSubscribe(option?.id ?? null)} />
                </div>
                <Checkbox
                    size="1.25rem"
                    radius="5px"
                    label="Invia mail di notifica"
                    labelPosition="end"
                    value={sendSubscriptionMail}
                    onChange={(checked) => setSendSubscriptionMail(checked)}
                />

                </div>
            </Dialog>
        </HeaderFooterLayout>
    )

}

export default Course
